import type {
  PostFillInTheBlanksInteractiveQuestionRequestBody,
  PostInteractiveQuestionAnswerRequestBody,
  PostOpenInteractiveQuestionAnswerRequestBody,
  PostShortAnswerInteractiveQuestionRequestBody,
  PostWidgetInteractiveQuestionRequestBody,
} from '@genially/contracts';
import { TrackingInteractiveQuestionType } from '@genially/contracts';

import type { PlayPostMessageInteractiveQuestion } from '../../../../../../../shared/domain/types/PlayPostMessageInteractiveQuestion';
import { bankAnswerIndexFromId } from './bankAnswerText';

/**
 * One line of the expanded detail of a row. Options with no text of their own are
 * reported by position instead: the panel is the one that knows how to say "Option 3".
 */
export type AnswerBullet = { text: string } | { optionPosition: number };

type DocumentAnswer = { Id: string; Text?: string; CorrectOrder?: number };

const documentAnswers = (
  question: PlayPostMessageInteractiveQuestion,
): DocumentAnswer[] =>
  'Answers' in question ? ((question.Answers as DocumentAnswer[]) ?? []) : [];

const bulletForAnswerId = (
  answers: DocumentAnswer[],
  answerId: string,
): AnswerBullet | undefined => {
  const index = answers.findIndex(({ Id }) => Id === answerId);

  if (index === -1) {
    return undefined;
  }

  const { Text } = answers[index];

  return Text ? { text: Text } : { optionPosition: index + 1 };
};

const isBullet = (bullet: AnswerBullet | undefined): bullet is AnswerBullet => !!bullet;

/**
 * What a single person answered, as the bullets the expanded row shows. Deliberately not
 * "was it right": the badge already says that, and the design does not show the correct
 * answer — see the ficha. Returns an empty list when there is nothing to unfold, which is
 * also what tells the row not to draw a chevron.
 */
export const describeUserAnswer = ({
  question,
  answer,
  resolveBankAnswerText,
}: {
  question: PlayPostMessageInteractiveQuestion;
  answer: PostInteractiveQuestionAnswerRequestBody;
  /**
   * Random questions only: the drawn bank question's option texts, by answer id. Their
   * options are not in the document, so without it the ids resolve to nothing — which is
   * also the graceful fallback while the bank is still being fetched, or if it fails.
   */
  resolveBankAnswerText?: (answerId: string) => string | undefined;
}): AnswerBullet[] => {
  const answers = documentAnswers(question);

  const bulletFor = (answerId: string): AnswerBullet | undefined => {
    if (!resolveBankAnswerText) {
      return bulletForAnswerId(answers, answerId);
    }

    const text = resolveBankAnswerText(answerId);

    return text ? { text } : undefined;
  };

  switch (answer.type) {
    case TrackingInteractiveQuestionType.OpenAnswer:
    case TrackingInteractiveQuestionType.ShortAnswer: {
      const { answerText } = answer as
        | PostOpenInteractiveQuestionAnswerRequestBody
        | PostShortAnswerInteractiveQuestionRequestBody;

      return answerText ? [{ text: answerText }] : [];
    }

    case TrackingInteractiveQuestionType.FillInTheBlanks: {
      const { userAnswer } = answer as PostFillInTheBlanksInteractiveQuestionRequestBody;
      // A bank blank carries its place in its own derived id; a document blank keeps it
      // in `CorrectOrder`.
      const orderOf = (blankId: string) =>
        resolveBankAnswerText
          ? bankAnswerIndexFromId(blankId)
          : (answers.find(({ Id }) => Id === blankId)?.CorrectOrder ?? 0);

      // Sorted by the position of the blank in the sentence, not by the order the keys
      // happen to sit in the record: what the host reads has to match what the
      // participant filled in, left to right.
      return Object.entries(userAnswer ?? {})
        .sort(([blankIdA], [blankIdB]) => orderOf(blankIdA) - orderOf(blankIdB))
        .map(([, answerId]) => bulletFor(answerId))
        .filter(isBullet);
    }

    case TrackingInteractiveQuestionType.Widget: {
      const { userAnswer } = answer as PostWidgetInteractiveQuestionRequestBody;
      const { detail, answerText, score } = userAnswer ?? {};

      if (detail?.type === 'string') {
        return detail.value ? [{ text: detail.value }] : [];
      }

      if (detail?.type === 'stringList') {
        return detail.value.map(text => ({ text }));
      }

      if (answerText) {
        return [{ text: answerText }];
      }

      // A rating scale reports nothing but the score, and the value someone gave is
      // exactly what the row is meant to unfold.
      return score === undefined ? [] : [{ text: `${score.value} / ${score.max}` }];
    }

    // The read-path envelope, which Play never sends: an answer to a drawn question
    // travels under the drawn type itself (see buildRandomAnswerWirePayload), so it is
    // the cases above that spell out a random question, with `resolveBankAnswerText`.
    case TrackingInteractiveQuestionType.Random:
      return [];

    // Everything left picks options: the sent order is meaningful in Sort and harmless
    // everywhere else, so it is preserved as it arrived.
    default: {
      const { answerIds } = answer as { answerIds?: string[] };

      return (answerIds ?? []).map(bulletFor).filter(isBullet);
    }
  }
};
